import { useState } from 'react';
import { requestCode, verifyCode } from '../services/webReportApi';
import type { WebReportMeta } from '../types/webReport';
import styles from './EmailGate.module.css';

interface Props {
  slug: string;
  meta: WebReportMeta | null;
  onUnlock: () => Promise<void>;
}

type Stage = 'enter-email' | 'enter-code';

export function EmailGate({ slug, meta, onUnlock }: Props) {
  const [stage, setStage] = useState<Stage>('enter-email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const brandName = meta?.advisor?.company || meta?.advisor?.name || 'PlanScout';
  const logoUrl = meta?.advisor?.companyLogoUrl || null;

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await requestCode(slug, email);
      setStage('enter-code');
      setInfo(
        "If your email is on file, a 6-digit code is on its way. Check your inbox."
      );
    } catch (err: any) {
      setError(messageFor(err?.message));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyCode(slug, code);
      await onUnlock();
    } catch (err: any) {
      const messageCode = err?.message || '';
      if (messageCode === 'access-denied') {
        setError('That code is invalid or expired. Request a new one.');
      } else {
        setError(messageFor(messageCode));
      }
      setSubmitting(false);
    }
  }

  async function handleResend() {
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await requestCode(slug, email);
      setInfo('A new code has been sent. Check your inbox.');
    } catch (err: any) {
      setError(messageFor(err?.message));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        {logoUrl ? (
          <img className={styles.logo} src={logoUrl} alt={brandName} />
        ) : (
          <div className={styles.brand}>{brandName.toUpperCase()}</div>
        )}

        {stage === 'enter-email' ? (
          <>
            <h1 className={styles.title}>View your report</h1>
            <p className={styles.body}>
              Enter the email associated with your plan. We&rsquo;ll send you a one-time code to verify it&rsquo;s you.
            </p>
            <form onSubmit={handleEmailSubmit} className={styles.form}>
              <label htmlFor="email" className={styles.label}>Email address</label>
              <input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={styles.input}
                placeholder="you@example.com"
                disabled={submitting}
              />
              {error && <div className={styles.error} role="alert">{error}</div>}
              <button
                type="submit"
                className={styles.button}
                disabled={submitting || !email}
              >
                {submitting ? 'Sending…' : 'Send me a code'}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className={styles.title}>Enter your code</h1>
            <p className={styles.body}>
              {info ?? 'Enter the 6-digit code we sent to your email.'}
            </p>
            <form onSubmit={handleCodeSubmit} className={styles.form}>
              <label htmlFor="code" className={styles.label}>Verification code</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={styles.input}
                placeholder="123456"
                disabled={submitting}
                aria-label="6-digit code"
              />
              {error && <div className={styles.error} role="alert">{error}</div>}
              <button
                type="submit"
                className={styles.button}
                disabled={submitting || code.length !== 6}
              >
                {submitting ? 'Verifying…' : 'Verify'}
              </button>
            </form>
            <button
              type="button"
              className={styles.linkButton}
              onClick={handleResend}
              disabled={submitting}
            >
              Resend code
            </button>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => {
                setStage('enter-email');
                setCode('');
                setError(null);
                setInfo(null);
              }}
              disabled={submitting}
            >
              Use a different email
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function messageFor(code: string | undefined): string {
  switch (code) {
    case 'rate-limited':
      return 'Too many code requests. Try again in a few minutes.';
    case 'expired':
      return 'This report has expired. Please contact your advisor for a new link.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
